import React from "react";

import NewFooter from "../reusables/new-footer/NewFooter";
import "./leads-comp.scss";

// The application opens in a new tab on Tally's hosted page. Embedding the form
// inline broke click hit-testing inside the framework's fixed-position `.slide`,
// and a popup is cramped on mobile — Tally's full-page form is mobile-optimized
// and reliable.
const TALLY_FORM_URL = "https://tally.so/r/xXz1Qo";
const WAITLIST_URL = "https://tally.so/r/rjNEOl";

// Set to true when a new cohort opens: the hero CTA and the bottom section switch
// from the waitlist back to the application form.
const APPLICATIONS_OPEN = false;

function LeadsComp() {
  const scrollToSection = (e, id) => {
    e.preventDefault();
    const el = global.window.document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="slide whiteSlide kenBurns leads-comp">
      {/* HERO */}
      <div className="leads-comp__hero">
        <h6 className="ae-1">A SIX-WEEK DESIGN CLINIC</h6>
        <h1 className="ae-2 largest">Take the leap from senior to lead.</h1>
        <p className="ae-3 largest">
          You&#39;ve mastered the craft as an excellent senior designer. Now
          leading demands something else. The Unflux Leads helps you make that
          move with structure, not guesswork.
        </p>
        <div className="hero-cta ae-4">
          <a
            className="leads-btn"
            href={APPLICATIONS_OPEN ? TALLY_FORM_URL : WAITLIST_URL}
            target="_blank"
            rel="noreferrer"
          >
            {APPLICATIONS_OPEN ? "Apply for a seat" : "Join the next cohort"}
          </a>
          <a
            className="leads-btn ghost"
            href="#what"
            onClick={(e) => scrollToSection(e, "what")}
          >
            See how it works
          </a>
        </div>
      </div>

      {/* THE GAP */}
      <section className="leads-section">
        <div className="leads-container">
          <div className="leads-band">
            <div className="band-grid">
              <p className="quote">
                Most senior designers are never taught to lead. They&#39;re
                handed a team and left to figure it out.
              </p>
              <div>
                <p>
                  The skills that make someone an exceptional senior designer
                  are not the skills that make them an effective lead. The job
                  becomes less about the craft, and more about the structure
                  that scales the craft. And almost no one is taught how to
                  build that structure.
                </p>
                <p>
                  That gap is where careers stall. Not for lack of talent. For
                  lack of a way through. Hence, The Unflux Leads.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT THIS IS */}
      <section className="leads-section" id="what">
        <div className="leads-container">
          <div className="section-label">What this is</div>
          <h2 className="large">A clinic, not a course.</h2>
          <p className="lede large">
            Small, candid, and built around the real challenges you bring into
            the room. Over six weeks we work through the three pillars that this
            transition actually demands — and you leave with a concrete plan to
            bring structure to your own design organization.
          </p>
          <div className="pillars">
            <div className="pillar">
              <div className="num">01</div>
              <h3>Product</h3>
              <p>
                Design is no longer the output – it&#39;s the lever. Influence
                strategy through design, prioritize what matters, and measure
                the impact you drive.
              </p>
            </div>
            <div className="pillar">
              <div className="num">02</div>
              <h3>Process</h3>
              <p>
                Build the systems behind great design – reviews, ops, rituals,
                and hiring. This is the foundation that scales quality beyond
                you and any one person.
              </p>
            </div>
            <div className="pillar">
              <div className="num">03</div>
              <h3>People</h3>
              <p>
                Develop skills that grow people, and learn to influence without
                authority. These are the hard conversations leadership is
                actually made of.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHO SHOULD APPLY */}
      <section className="leads-section">
        <div className="leads-container">
          <div className="cols">
            <div>
              <div className="section-label">Who should apply</div>
              <h2 className="large">
                You&#39;re on the edge of leading — or already there and finding
                your footing.
              </h2>
              <p className="lede large">
                This is for senior product designers ready to do the work of a
                leader, not just hold the title. We&#39;re selective because the
                room only works when everyone in it belongs.
              </p>
            </div>
            <ul className="checklist">
              <li>
                <b>You&#39;re close to the transition.</b>
                <span>
                  About to step up, newly leading, or stuck just below it with
                  the gaps live and urgent.
                </span>
              </li>
              <li>
                <b>You already lead before the title.</b>
                <span>
                  You&#39;re influencing how your team works, even without the
                  authority to.
                </span>
              </li>
              <li>
                <b>You&#39;re coachable.</b>
                <span>Open to candid feedback in a small, honest room.</span>
              </li>
              <li>
                <b>You&#39;re embedded in a team.</b>
                <span>With real problems your capstone plan can map onto.</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* WHAT YOU LEAVE WITH */}
      <section className="leads-section">
        <div className="leads-container">
          <div className="section-label">What you leave with</div>
          <h2 className="large">
            A design-infrastructure plan for your own org.
          </h2>
          <p className="lede large">
            The clinic builds toward a single tangible outcome: a concrete plan
            to bring structure to the design organization you actually work in.
            You leave with direction, not just inspiration.
          </p>
        </div>
      </section>

      {/* FORMAT */}
      <section className="leads-section">
        <div className="leads-container">
          <div className="section-label">The format</div>
          <h2 className="large">Built to fit a working designer&#39;s week.</h2>
          <div className="facts">
            <div className="fact">
              <strong>6 weeks</strong>
              <span>The full clinic, start to finish.</span>
            </div>
            <div className="fact">
              <strong>2 x 1-Hour</strong>
              <span>Two live sessions each week.</span>
            </div>
            <div className="fact">
              <strong>10 seats</strong>
              <span>Handpicked for real conversations.</span>
            </div>
          </div>
        </div>
      </section>

      {/* WHO'S RUNNING IT */}
      <section className="leads-section">
        <div className="leads-container">
          <div className="section-label">Who&#39;s running it</div>
          <h2 className="large">Taught by someone who does the job.</h2>
          <div className="founder">
            <img
              className="avatar"
              src="/assets/img/elias-awili.jpg"
              alt="Elias Awili"
            />
            <p>
              With 7+ years of global design leadership experience,{" "}
              <b>Elias Awili</b> currently leads a multidisciplinary team of over
              15 designers and design leads. The Unflux Leads brings together the
              lessons, frameworks, and hard-won insights from that journey, built
              on one belief:{" "}
              <b>
                the leap from senior to lead isn&#39;t just about becoming better
                at design. It&#39;s about learning to lead through others.
              </b>{" "}
              <a href="https://eliasawili.com" target="_blank" rel="noreferrer">
                Learn more
              </a>
            </p>
          </div>
        </div>
      </section>

      {APPLICATIONS_OPEN ? (
        /* APPLY */
        <section className="leads-section" id="apply">
          <div className="leads-container">
            <div className="leads-apply">
              <div className="section-label">Apply</div>
              <h2 className="large">Ten seats. Tell us why one is yours.</h2>
              <p className="lede large">
                The application takes real thought — that&#39;s by design. We
                read for how you think, not how polished you sound. It takes
                about 10 minutes.
              </p>
              <div className="apply-cta">
                <a
                  className="leads-btn"
                  href={TALLY_FORM_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  Start your application
                </a>
              </div>
              <p className="apply-note">
                <b>Free for this first cohort.</b> We read every application
                carefully and reply to everyone.
              </p>
            </div>
          </div>
        </section>
      ) : (
        /* WAITLIST */
        <section className="leads-section" id="waitlist">
          <div className="leads-container">
            <div className="leads-apply">
              <div className="section-label">Waitlist</div>
              <h2 className="large">
                The next cohort is coming. Be first to know.
              </h2>
              <p className="lede large">
                Our first cohort is complete. Join the waitlist and we&#39;ll
                tell you as soon as applications open for the next one. It
                takes less than a minute.
              </p>
              <div className="apply-cta">
                <a
                  className="leads-btn"
                  href={WAITLIST_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  Join the waitlist
                </a>
              </div>
              <p className="apply-note">
                <b>Seats are limited.</b> People on the waitlist hear about the
                next cohort first.
              </p>
            </div>
          </div>
        </section>
      )}

      <NewFooter />
    </section>
  );
}

export default LeadsComp;
